import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Icon,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from "@nexus/ui";
import type { IconName } from "@nexus/ui";
import { buildCriterionTrends } from "@nexus/contracts";
import type {
  CriterionTrend,
  CriterionTrendDirection,
  CriterionTrendPoint,
} from "@nexus/contracts";

import { canAnimate } from "../../lib/motion";
import type { InsightsHeatmap } from "../../types/preflight.types";
import { CriterionLabel } from "../CriterionLabel/CriterionLabel";

/** Fixed geometry so every row's sparkline shares one baseline and one scale. */
const SPARK_WIDTH = 180;
const SPARK_HEIGHT = 28;
/** Keeps the outermost dots and the 0%/100% extremes off the edges. */
const SPARK_PADDING = 4;
const DOT_RADIUS = 1.75;
const LATEST_DOT_RADIUS = 3;
/** Hovered/focused point — still small enough not to collide with neighbours. */
const HOVER_DOT_RADIUS = 4.5;
/**
 * Invisible hit area around each dot. Full 44px targets would overlap on a 180px
 * sparkline; 20px keeps points separable while still easier to aim than the painted dot.
 */
const HIT_TARGET_PX = 20;

/** Line draws left→right so the chronology reads before the dots settle. */
const SPARK_DRAW_MS = 500;
const SPARK_DOT_MS = 200;
const SPARK_BASELINE_MS = 200;
/** Caps total list stagger so a long criteria list doesn't feel interminable. */
const SPARK_ROW_STAGGER_MS = 45;
const SPARK_MAX_STAGGER_INDEX = 7;
/** Ease-out quint — same curve as the creative-health donut sweep. */
const SPARK_EASE = "cubic-bezier(0.22, 1, 0.36, 1)";

/** Pass-rate deltas carry their sign, matching the average-score trend tooltip. */
function formatSignedDelta(delta: number): string {
  return delta > 0 ? `+${delta}` : String(delta);
}

/** Theme-aware colours — SVG strokes use `currentColor` so dark mode stays readable. */
const DIRECTION_CLASS: Record<CriterionTrendDirection, string> = {
  IMPROVED: "text-success",
  REGRESSED: "text-error",
  /** Foreground (not muted) — muted-foreground washes out on dark card surfaces. */
  FLAT: "text-foreground/80",
};

const DIRECTION_SWATCH: Record<CriterionTrendDirection, string> = {
  IMPROVED: "bg-success",
  REGRESSED: "bg-error",
  FLAT: "bg-foreground/80",
};

/** Chevrons only when the criterion moved — holding has no glyph (avoids clutter next to the delta). */
const DIRECTION_ICON: Partial<Record<CriterionTrendDirection, IconName>> = {
  IMPROVED: "chevron-up",
  REGRESSED: "chevron-down",
};

const LEGEND_DIRECTIONS: CriterionTrendDirection[] = [
  "IMPROVED",
  "REGRESSED",
  "FLAT",
];

interface PlottedPoint extends CriterionTrendPoint {
  x: number;
  /** Null where the criterion had no verdict in that report. */
  y: number | null;
}

function plot(points: readonly CriterionTrendPoint[]): PlottedPoint[] {
  const span = SPARK_WIDTH - SPARK_PADDING * 2;
  const height = SPARK_HEIGHT - SPARK_PADDING * 2;
  const step = points.length > 1 ? span / (points.length - 1) : 0;

  return points.map((point, index) => ({
    ...point,
    x: SPARK_PADDING + step * index,
    y:
      point.passRate == null
        ? null
        : SPARK_PADDING + height - (point.passRate / 100) * height,
  }));
}

/**
 * Contiguous runs of observed points. A report where the criterion never came up is a gap in
 * the line, not a dip through zero — joining across it would invent a verdict.
 */
function segmentsOf(points: readonly PlottedPoint[]): PlottedPoint[][] {
  const segments: PlottedPoint[][] = [];
  let current: PlottedPoint[] = [];

  for (const point of points) {
    if (point.y == null) {
      if (current.length > 0) segments.push(current);
      current = [];
      continue;
    }
    current.push(point);
  }
  if (current.length > 0) segments.push(current);

  return segments;
}

function sparkRowDelayMs(rowIndex: number): number {
  return Math.min(rowIndex, SPARK_MAX_STAGGER_INDEX) * SPARK_ROW_STAGGER_MS;
}

/** Delay so each dot appears as the draw reaches its x — chronology, not decoration. */
function sparkPointDelayMs(rowDelayMs: number, index: number, count: number): number {
  if (count <= 1) return rowDelayMs;
  return rowDelayMs + (index / (count - 1)) * SPARK_DRAW_MS;
}

function SparkPointTooltip({
  point,
  previous,
  isDrawn,
  onHighlight,
  onClearHighlight,
}: {
  point: PlottedPoint;
  previous: PlottedPoint | null;
  isDrawn: boolean;
  onHighlight: () => void;
  onClearHighlight: () => void;
}) {
  const { t } = useTranslation();
  const rate = point.passRate!;
  const delta =
    previous?.passRate != null ? rate - previous.passRate : null;
  // A zero move is noise in the tip — the pass rate already says it held.
  const signedDelta =
    delta == null || delta === 0 ? null : formatSignedDelta(delta);

  const ariaLabel =
    signedDelta != null && previous
      ? t("preflight.trends.pointAriaWithChange", {
          report: point.label,
          rate,
          count: point.creativeCount,
          delta: signedDelta,
          previous: previous.label,
        })
      : t("preflight.trends.pointAria", {
          report: point.label,
          rate,
          count: point.creativeCount,
        });

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={ariaLabel}
          onMouseEnter={onHighlight}
          onFocus={onHighlight}
          onBlur={onClearHighlight}
          className={cn(
            "absolute z-10 -translate-x-1/2 -translate-y-1/2 cursor-help rounded-full",
            "outline-none focus-visible:ring-ring/40 focus-visible:ring-2",
            "transition-opacity duration-150 ease-out motion-reduce:transition-none",
            isDrawn ? "opacity-100" : "opacity-0",
          )}
          style={{
            left: point.x,
            top: point.y!,
            width: HIT_TARGET_PX,
            height: HIT_TARGET_PX,
          }}
        />
      </TooltipTrigger>
      <TooltipContent
        side="top"
        className="flex flex-col gap-0.5 px-3 py-2 text-left"
      >
        <span className="font-medium">{point.label}</span>
        <span className="tabular-nums">
          {t("preflight.trends.pointPassRate", { rate })}
        </span>
        <span className="text-tooltip-foreground/80 flex flex-wrap items-center gap-1 font-normal">
          <span className="tabular-nums">
            {t("preflight.marketOverview.trendTooltipCreatives", {
              count: point.creativeCount,
            })}
          </span>
          {signedDelta != null && previous ? (
            <>
              <span aria-hidden>{t("preflight.middot")}</span>
              <span className="tabular-nums">
                {t("preflight.marketOverview.trendTooltipChange", {
                  delta: signedDelta,
                  label: previous.label,
                })}
              </span>
            </>
          ) : null}
        </span>
      </TooltipContent>
    </Tooltip>
  );
}

function Sparkline({
  trend,
  rowIndex,
}: {
  trend: CriterionTrend;
  rowIndex: number;
}) {
  const [isDrawn, setIsDrawn] = useState(() => !canAnimate());
  const [isDashCleared, setIsDashCleared] = useState(() => !canAnimate());
  const [hoveredColumnId, setHoveredColumnId] = useState<string | null>(null);
  const points = plot(trend.points);
  const observed = points.filter((point) => point.y != null);
  const latest = observed.at(-1);
  const rowDelayMs = sparkRowDelayMs(rowIndex);
  const motion = canAnimate();

  useEffect(() => {
    if (!canAnimate()) {
      setIsDrawn(true);
      setIsDashCleared(true);
      return;
    }

    // Two frames so the undrawn stroke paints before dashoffset transitions to 0.
    let innerRaf = 0;
    let clearTimer = 0;
    const outerRaf = requestAnimationFrame(() => {
      innerRaf = requestAnimationFrame(() => {
        setIsDrawn(true);
        // Keep the unit dash until the reveal finishes, then drop it for a solid stroke.
        clearTimer = window.setTimeout(
          () => setIsDashCleared(true),
          rowDelayMs + SPARK_DRAW_MS + 50,
        );
      });
    });
    return () => {
      cancelAnimationFrame(outerRaf);
      cancelAnimationFrame(innerRaf);
      window.clearTimeout(clearTimer);
    };
  }, [rowDelayMs]);

  return (
    <div
      className="relative shrink-0"
      style={{ width: SPARK_WIDTH, height: SPARK_HEIGHT }}
      onMouseLeave={() => setHoveredColumnId(null)}
    >
      <svg
        aria-hidden
        viewBox={`0 0 ${SPARK_WIDTH} ${SPARK_HEIGHT}`}
        width={SPARK_WIDTH}
        height={SPARK_HEIGHT}
        className={cn(
          "pointer-events-none absolute inset-0 overflow-visible",
          DIRECTION_CLASS[trend.direction],
        )}
      >
        {/* Starting pass rate — a quiet reference so the line's height reads as a move. */}
        <line
          x1={0}
          x2={SPARK_WIDTH}
          y1={observed[0]?.y ?? SPARK_HEIGHT / 2}
          y2={observed[0]?.y ?? SPARK_HEIGHT / 2}
          stroke="var(--border)"
          strokeWidth={1}
          strokeDasharray="3 3"
          style={{
            opacity: isDrawn ? 1 : 0,
            transition: motion
              ? `opacity ${SPARK_BASELINE_MS}ms ${SPARK_EASE} ${rowDelayMs}ms`
              : undefined,
          }}
        />
        {segmentsOf(points).map((segment) => (
          <polyline
            key={segment[0]!.columnId}
            points={segment.map((point) => `${point.x},${point.y}`).join(" ")}
            fill="none"
            stroke="currentColor"
            strokeWidth={1.5}
            strokeLinecap="round"
            strokeLinejoin="round"
            // Unit length so dashoffset 1→0 draws the segment without measuring path length.
            pathLength={1}
            strokeDasharray={isDashCleared ? undefined : 1}
            strokeDashoffset={isDrawn ? 0 : 1}
            style={{
              transition: motion
                ? `stroke-dashoffset ${SPARK_DRAW_MS}ms ${SPARK_EASE} ${rowDelayMs}ms`
                : undefined,
            }}
          />
        ))}
        {observed.map((point, index) => {
          const isHovered = point.columnId === hoveredColumnId;
          const baseRadius =
            point === latest ? LATEST_DOT_RADIUS : DOT_RADIUS;
          return (
            <circle
              key={point.columnId}
              cx={point.x}
              cy={point.y!}
              r={isHovered ? HOVER_DOT_RADIUS : baseRadius}
              fill={
                point === latest || isHovered ? "currentColor" : "var(--card)"
              }
              stroke="currentColor"
              strokeWidth={1.5}
              style={{
                opacity: isDrawn ? 1 : 0,
                transition: motion
                  ? `opacity ${SPARK_DOT_MS}ms ${SPARK_EASE} ${sparkPointDelayMs(rowDelayMs, index, observed.length)}ms`
                  : undefined,
              }}
            />
          );
        })}
      </svg>
      {observed.map((point, index) => (
        <SparkPointTooltip
          key={point.columnId}
          point={point}
          previous={index > 0 ? observed[index - 1]! : null}
          isDrawn={isDrawn}
          onHighlight={() => setHoveredColumnId(point.columnId)}
          onClearHighlight={() => setHoveredColumnId(null)}
        />
      ))}
    </div>
  );
}

function TrendRow({
  trend,
  rowIndex,
}: {
  trend: CriterionTrend;
  rowIndex: number;
}) {
  const { t } = useTranslation();
  const signedDelta = formatSignedDelta(trend.delta);
  const summaryLabel = t(`preflight.trends.summaryTooltip.${trend.direction}`, {
    from: trend.first,
    to: trend.latest,
    delta: Math.abs(trend.delta),
  });

  return (
    <li className="flex items-center gap-3 px-2 py-2">
      {/* A fixed name column so every sparkline shares one left edge and one scale. */}
      <CriterionLabel
        name={trend.name}
        definition={trend.definition}
        className="text-foreground w-48 shrink-0 text-sm"
      />
      <Sparkline trend={trend} rowIndex={rowIndex} />
      {/* Range + signed move: one tip explains both so the pair is not read as raw numbers. */}
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={t(`preflight.trends.rowAria.${trend.direction}`, {
              criterion: trend.name,
              from: trend.first,
              to: trend.latest,
              delta: Math.abs(trend.delta),
            })}
            className={cn(
              "ml-auto flex shrink-0 cursor-help items-center gap-2 rounded-sm",
              "outline-none focus-visible:ring-ring/40 focus-visible:ring-2",
            )}
          >
            <span className="text-muted-foreground text-xs tabular-nums">
              {t("preflight.trends.range", {
                from: trend.first,
                to: trend.latest,
              })}
            </span>
            <span
              className={cn(
                "inline-flex items-center gap-0.5 text-xs font-semibold tabular-nums",
                DIRECTION_CLASS[trend.direction],
              )}
            >
              {DIRECTION_ICON[trend.direction] ? (
                <Icon
                  name={DIRECTION_ICON[trend.direction]!}
                  size="xs"
                  color="inherit"
                  aria-hidden
                />
              ) : null}
              <span>{t("preflight.trends.delta", { delta: signedDelta })}</span>
            </span>
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs text-left">
          {summaryLabel}
        </TooltipContent>
      </Tooltip>
    </li>
  );
}

function TrendLegend() {
  const { t } = useTranslation();

  return (
    <div
      role="group"
      className="text-muted-foreground flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px]"
      aria-label={t("preflight.trends.legendAria")}
    >
      {LEGEND_DIRECTIONS.map((direction) => {
        const icon = DIRECTION_ICON[direction];
        return (
          <span key={direction} className="flex items-center gap-1.5">
            <span
              aria-hidden
              className={cn(
                "inline-block h-0.5 w-3 rounded-full",
                DIRECTION_SWATCH[direction],
              )}
            />
            {icon ? (
              <Icon
                name={icon}
                size="xs"
                color="inherit"
                className={cn("shrink-0", DIRECTION_CLASS[direction])}
                aria-hidden
              />
            ) : null}
            <span>{t(`preflight.trends.legend.${direction}`)}</span>
          </span>
        );
      })}
      <span className="flex items-center gap-1.5">
        <span
          aria-hidden
          className="border-foreground/70 size-1.5 rounded-full border bg-card"
        />
        <span>{t("preflight.trends.legend.openDot")}</span>
      </span>
      <span className="flex items-center gap-1.5">
        <span
          aria-hidden
          className="bg-foreground/80 size-2 rounded-full"
        />
        <span>{t("preflight.trends.legend.filledDot")}</span>
      </span>
      <span className="flex items-center gap-1.5">
        <span
          aria-hidden
          className="border-border inline-block w-3 border-t border-dashed"
        />
        <span>{t("preflight.trends.legend.baseline")}</span>
      </span>
      <span className="flex items-center gap-1.5">
        <span aria-hidden className="flex items-center gap-0.5">
          <span className="bg-foreground/70 size-1.5 rounded-full" />
          <span className="bg-foreground/35 h-px w-2" />
          <span className="bg-foreground/70 size-1.5 rounded-full" />
        </span>
        <span>{t("preflight.trends.legend.gap")}</span>
      </span>
    </div>
  );
}

export interface CriterionTrendsProps {
  /** The aggregate's `criterionHistory` — criteria pivoted by report, oldest first. */
  history: InsightsHeatmap;
  className?: string;
}

/**
 * Whether each criterion is holding, improving, or slipping report over report (R20a). The
 * average-score trend says the score moved; this says which criteria moved it, which is the
 * only view that answers whether last round's notes actually stuck.
 *
 * Renders nothing until two reports are in scope — a snapshot has no trajectory.
 */
export function CriterionTrends({ history, className }: CriterionTrendsProps) {
  const { t } = useTranslation();
  const trends = useMemo(() => buildCriterionTrends(history), [history]);

  if (trends.length === 0) return null;

  const reportCount = trends[0]!.points.length;

  return (
    <TooltipProvider delayDuration={250} skipDelayDuration={100}>
      <div className={cn("flex flex-col gap-3", className)}>
        <div className="flex flex-col gap-2">
          <p className="text-muted-foreground text-sm">
            {t("preflight.trends.description", { count: reportCount })}
          </p>
          <TrendLegend />
        </div>
        <ul className="divide-border -mx-2 flex max-h-72 flex-col divide-y overflow-y-auto">
          {trends.map((trend, rowIndex) => (
            <TrendRow key={trend.key} trend={trend} rowIndex={rowIndex} />
          ))}
        </ul>
      </div>
    </TooltipProvider>
  );
}
